/**
 * SettlementIntelligenceTab — PR G
 *
 * Renders game theory analysis seeded from the latest War Room consensus run.
 * Calls GET /api/v1/game-theory/war-room/{draftId} and displays:
 *   - Nash equilibrium + recommended settlement target
 *   - ZOPA range with a visual bar
 *   - Policy cap context (cap-clipped MC note when applicable)
 *   - Strategic recommendations list
 *   - Input provenance (what values were pulled from the consensus doc)
 *
 * Shown in the "Settlement" tab on the WarRoom deliberation page.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Target,
  Scale,
  TrendingUp,
  AlertTriangle,
  Info,
  RefreshCw,
  Loader2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  getWarRoomGameTheory,
  type WarRoomGameTheoryResult,
  type GameTheoryRecommendation,
} from '@/services/deliberationApi';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { formatCurrency } from '@/utils/financialFormat';

// ---------------------------------------------------------------------------
// Types for consistency flags (Fix B)
// ---------------------------------------------------------------------------

interface ConsistencyFlag {
  severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  message?: string;
  interpretation?: string;
  status?: 'INCONSISTENT' | 'CONSISTENT' | 'UNKNOWN';
  [key: string]: any;
}

interface ConsistencyFlags {
  available?: boolean;
  mc_vs_forensic?: {
    status?: 'INCONSISTENT' | 'CONSISTENT' | 'UNKNOWN';
    severity?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
    interpretation?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${n.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetricCard({
  label,
  value,
  sub,
  accent = false,
  warn = false,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  warn?: boolean;
}) {
  return (
    <div
      className="rounded-lg border p-4"
      style={
        accent
          ? { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }
          : warn
          ? { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }
          : { backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }
      }
    >
      <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-muted)' }}>{label}</p>
      <p
        className="text-xl font-mono font-semibold"
        style={{ color: accent ? 'var(--color-needs-human-text)' : warn ? 'var(--color-needs-human-text)' : 'var(--color-text-primary)' }}
      >
        {value}
      </p>
      {sub && <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>{sub}</p>}
    </div>
  );
}

function ZopaBar({ floor, ceiling, nash, target }: { floor: number; ceiling: number; nash: number; target: number }) {
  const range = ceiling - floor;
  if (range <= 0) return null;

  const pinPct = (v: number) =>
    Math.max(0, Math.min(100, ((v - floor) / range) * 100));

  const nashPct = pinPct(nash);
  const targetPct = pinPct(target);

  return (
    <div className="mt-4">
      <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>Zone of Possible Agreement</p>
      <div className="relative h-6 rounded-full border overflow-visible" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
        {/* ZOPA fill */}
        <div className="absolute inset-0 rounded-full" style={{ backgroundColor: 'transparent' }} />

        {/* Nash pin */}
        <div
          className="absolute top-0 h-6 w-0.5"
          style={{ left: `${nashPct}%`, backgroundColor: 'var(--color-brand)' }}
          title={`Nash: ${formatCurrency(nash)}`}
        />
        {/* Target pin */}
        <div
          className="absolute top-0 h-6 w-0.5"
          style={{ left: `${targetPct}%`, backgroundColor: 'var(--color-brand-hover)' }}
          title={`Target: ${formatCurrency(target)}`}
        />
      </div>

      <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
        <span><FinancialValue value={floor} /></span>
        <div className="flex gap-4">
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }} />
            Nash <FinancialValue value={nash} />
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-brand-hover)' }} />
            Target <FinancialValue value={target} />
          </span>
        </div>
        <span><FinancialValue value={ceiling} /></span>
      </div>
    </div>
  );
}

function RecommendationRow({ rec, index }: { rec: GameTheoryRecommendation; index: number }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="border rounded-lg" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <span className="flex-shrink-0 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
          {rec.priority}
        </span>
        <span className="flex-1 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{rec.title}</span>
        <span className="text-xs mr-2 capitalize" style={{ color: 'var(--color-text-dim)' }}>{rec.category}</span>
        {open ? (
          <ChevronDown className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
        ) : (
          <ChevronRight className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
        )}
      </button>
      {open && (
        <div className="px-4 pb-3 text-sm border-t pt-3" style={{ color: 'var(--color-text-secondary)', borderColor: 'var(--color-border-sub)' }}>
          {rec.recommendation}
          {rec.rationale && (
            <p className="mt-1 text-xs italic" style={{ color: 'var(--color-text-dim)' }}>{rec.rationale}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface Props {
  reportId: string;
}

type LoadState = 'idle' | 'loading' | 'ok' | 'no_run' | 'no_sensitivity' | 'error';

export function SettlementIntelligenceTab({ reportId }: Props) {
  const [state, setState] = useState<LoadState>('idle');
  const [data, setData] = useState<WarRoomGameTheoryResult | null>(null);
  const [style, setStyle] = useState('competitive');
  const [showSources, setShowSources] = useState(false);

  const load = useCallback(
    async (negotiationStyle: string) => {
      setState('loading');
      setData(null);
      try {
        const result = await getWarRoomGameTheory(reportId, negotiationStyle);
        setData(result);
        setState('ok');
      } catch (err: any) {
        const status = err?.response?.status ?? err?.status;
        if (status === 404) setState('no_run');
        else if (status === 422) setState('no_sensitivity');
        else setState('error');
      }
    },
    [reportId],
  );

  useEffect(() => {
    load(style);
  }, [load, style]);

  // ---------- loading ----------
  if (state === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3" style={{ color: 'var(--color-text-muted)' }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
        <p className="text-sm">Running settlement analysis…</p>
      </div>
    );
  }

  // ---------- empty states ----------
  if (state === 'no_run') {
    return (
      <div className="text-center py-16" style={{ color: 'var(--color-text-muted)' }}>
        <Scale className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--color-text-ghost)' }} />
        <p className="text-sm font-medium">No War Room run found</p>
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
          Trigger an Agent War Room analysis first — game theory uses the MC positions from the
          latest consensus run.
        </p>
      </div>
    );
  }

  if (state === 'no_sensitivity') {
    return (
      <div className="text-center py-16" style={{ color: 'var(--color-text-muted)' }}>
        <AlertTriangle className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--color-brand)' }} />
        <p className="text-sm font-medium">Sensitivity report not available</p>
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
          Re-trigger the War Room to generate Monte Carlo positions.
        </p>
      </div>
    );
  }

  if (state === 'error' || !data) {
    return (
      <div className="text-center py-16" style={{ color: 'var(--color-text-muted)' }}>
        <AlertTriangle className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--color-needs-human-text)' }} />
        <p className="text-sm font-medium">Analysis failed</p>
        <button
          onClick={() => load(style)}
          className="mt-3 text-xs hover:underline"
          style={{ color: 'var(--color-needs-human-text)' }}
        >
          Retry
        </button>
      </div>
    );
  }

  const { settlement_range: sr, zopa, nash_equilibrium: nash, strategic_recommendations: recs, war_room_context: ctx } = data;

  return (
    <div className="space-y-6" data-testid="settlement-intelligence-tab">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5" style={{ color: 'var(--color-needs-human-text)' }} />
          <h2 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>Settlement Intelligence</h2>
          <span className="text-xs ml-1" style={{ color: 'var(--color-text-dim)' }}>
            War Room run {ctx.run_id} · {new Date(ctx.produced_at).toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Style selector */}
          <select
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            className="text-xs border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)' }}
            aria-label="Negotiation style"
          >
            <option value="aggressive">Aggressive</option>
            <option value="competitive">Competitive</option>
            <option value="cooperative">Cooperative</option>
            <option value="accommodating">Accommodating</option>
          </select>
          <button
            onClick={() => load(style)}
            className="p-1.5 transition-colors"
            style={{ color: 'var(--color-text-dim)' }}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Policy cap notice */}
      {ctx.policy_cap != null && (
        <div
          className="rounded-lg px-4 py-3 flex gap-3 items-start text-sm border"
          style={
            ctx.mc_p50_cap_clipped
              ? { backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }
              : { backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }
          }
          data-testid="policy-cap-notice"
        >
          {ctx.mc_p50_cap_clipped ? (
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
          ) : (
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
          )}
          <div>
            <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              Policy cap <FinancialValue value={ctx.policy_cap} />
            </span>
            {ctx.over_cap_probability_pct != null && (
              <span className="ml-2" style={{ color: 'var(--color-text-muted)' }}>
                · {pct(ctx.over_cap_probability_pct)} of MC scenarios exceed cap
              </span>
            )}
            {ctx.mc_p50_note && (
              <p className="mt-1 text-xs" style={{ color: 'var(--color-needs-human-text)' }}>{ctx.mc_p50_note}</p>
            )}
          </div>
        </div>
      )}

      {/* Consistency flags warning strip — Fix B */}
      {(() => {
        const flags = (data as any)?.consistency_flags as ConsistencyFlags | undefined;
        if (!flags || flags.available === false) return null;

        // Extract severity and message from mc_vs_forensic
        const mcVsForensic = flags.mc_vs_forensic;
        const status = mcVsForensic?.status;

        // Only render if there's an actual inconsistency (not CONSISTENT or UNKNOWN)
        if (!status || status === 'CONSISTENT' || status === 'UNKNOWN') return null;

        const severity = mcVsForensic?.severity;
        const message = mcVsForensic?.interpretation || mcVsForensic?.message || 'Inconsistency detected between Monte Carlo and forensic engine.';

        // Determine styling based on severity
        const isCritical = severity === 'CRITICAL';
        const bgColor = isCritical ? 'var(--color-status-adverse)' : 'var(--color-brand)';
        const textColor = 'var(--color-on-brand)';

        return (
          <div
            className="rounded-lg px-4 py-3 flex gap-3 items-start text-sm"
            style={{ backgroundColor: bgColor, color: textColor }}
            data-testid="consistency-flags-warning"
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-medium">
                {isCritical ? 'Critical: ' : 'Warning: '}
              </span>
              <span>{message}</span>
            </div>
          </div>
        );
      })()}

      {/* Top metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard
          label="Recommended Target"
          value={formatCurrency(sr.recommended_target)}
          sub="Attorney opening anchor"
          accent
        />
        <MetricCard
          label="Nash Equilibrium"
          value={formatCurrency(nash.point)}
          sub={`Stability: ${nash.stability.replace(/_/g, ' ')}`}
        />
        <MetricCard
          label="ZOPA Floor"
          value={formatCurrency(zopa.floor)}
          sub="Claimant reservation (walk-away)"
        />
        <MetricCard
          label="ZOPA Ceiling"
          value={formatCurrency(zopa.ceiling)}
          sub="Insurer reservation (max pay)"
        />
      </div>

      {/* ZOPA bar */}
      {zopa.exists && (
        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              Zone of Possible Agreement
            </p>
            <span className="text-xs" style={{ color: 'var(--color-text-dim)' }}>
              {formatCurrency(zopa.range)} range · midpoint {formatCurrency(zopa.midpoint)}
            </span>
          </div>
          <ZopaBar
            floor={zopa.floor}
            ceiling={zopa.ceiling}
            nash={nash.point}
            target={sr.recommended_target}
          />
          {zopa.analysis && (
            <p className="text-xs mt-3 italic" style={{ color: 'var(--color-text-muted)' }}>{zopa.analysis}</p>
          )}
        </div>
      )}

      {/* Nash explanation */}
      <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-4 w-4" style={{ color: 'var(--color-needs-human-text)' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Nash Bargaining Solution</p>
        </div>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{nash.explanation}</p>
        <div className="mt-3 grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Claimant surplus</p>
            <p className="text-sm font-mono font-semibold" style={{ color: 'var(--color-text-secondary)' }}><FinancialValue value={nash.claimant_surplus} /></p>
          </div>
          <div>
            <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Insurer surplus</p>
            <p className="text-sm font-mono font-semibold" style={{ color: 'var(--color-text-secondary)' }}><FinancialValue value={nash.insurer_surplus} /></p>
          </div>
          <div>
            <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Fairness index</p>
            <p className="text-sm font-mono font-semibold" style={{ color: 'var(--color-text-secondary)' }}>{nash.fairness_index.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Strategic recommendations */}
      {recs && recs.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4" style={{ color: 'var(--color-needs-human-text)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              Strategic Recommendations
              <span className="ml-2 text-xs font-normal" style={{ color: 'var(--color-text-dim)' }}>{recs.length} items</span>
            </p>
          </div>
          <div className="space-y-2">
            {recs.map((rec, i) => (
              <RecommendationRow key={rec.priority} rec={rec} index={i} />
            ))}
          </div>
        </div>
      )}

      {/* Input provenance (collapsible) */}
      <div className="border rounded-lg" style={{ borderColor: 'var(--color-border)' }}>
        <button
          onClick={() => setShowSources((s) => !s)}
          className="w-full flex items-center gap-2 px-4 py-3 text-sm transition-colors"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <Info className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1 text-left">Input provenance</span>
          {showSources ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        {showSources && (
          <div className="px-4 pb-4 pt-1 border-t space-y-1" style={{ borderColor: 'var(--color-border-sub)' }}>
            {Object.entries(ctx.input_sources).map(([k, v]) => (
              <div key={k} className="flex gap-2 text-xs">
                <span className="w-28 flex-shrink-0 font-mono" style={{ color: 'var(--color-text-dim)' }}>{k}</span>
                <span style={{ color: 'var(--color-text-secondary)' }}>{v}</span>
              </div>
            ))}
            <div className="flex gap-2 text-xs mt-2 pt-2 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
              <span className="w-28 flex-shrink-0 font-mono" style={{ color: 'var(--color-text-dim)' }}>style</span>
              <span className="capitalize" style={{ color: 'var(--color-text-secondary)' }}>{ctx.negotiation_style_used}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
